import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, MenuItem, Skeleton, Snackbar,
  Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { BsPencil, BsPlusLg, BsTrash } from 'react-icons/bs';

import axiosInstance from '../../axiosInstance';
import Fiche from '../Expert/Fiche';
import { STATUTS } from '../Tasks/taskDialog';
import { themeExpert } from '../../theme/expert';
import ChampDate from '../ChampDate/ChampDate';

const VIDE = {
  title: '', title_bis: '', comment: '', review_at: '', sender: '',
  receiver: '', copy_receiver: '', hierarchy: '', public: false,
  status: 'notstart',
};

const OUTILS = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ color: [] }, { background: [] }],
    ['link', 'clean'],
  ],
};

const statut = (code) => STATUTS.find((s) => s.code === code);

const dateCourte = (v) => (v ? new Date(v).toLocaleDateString('fr-BE') : null);

/** Une tâche type, prête à être posée sur un dossier. */
function TaskModelView() {
  const [modeles, setModeles] = useState([]);
  const [courriers, setCourriers] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [occupe, setOccupe] = useState(false);

  const [fiche, setFiche] = useState(null);
  const [aSupprimer, setASupprimer] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const [m, c] = await Promise.all([
        axiosInstance.get('/tasks_model/'),
        axiosInstance.get('/templates/'),
      ]);
      setModeles(m.data.results || m.data);
      setCourriers(c.data.results || c.data);
      setErreur('');
    } catch {
      setErreur("Les modèles n'ont pas pu être chargés.");
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const poser = (champ) => (e) => setFiche((etat) => ({
    ...etat,
    [champ]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  const estCourriel = fiche?.hierarchy === 'EMAIL';

  const appliquerCourrier = async (id) => {
    if (!id) return;
    try {
      const { data } = await axiosInstance.get(`/templates/${id}/`);
      setFiche((etat) => ({ ...etat, comment: data.content }));
    } catch {
      setErreur("Le modèle de courrier n'a pas pu être chargé.");
    }
  };

  const enregistrer = async (e) => {
    e.preventDefault();
    setOccupe(true);
    try {
      const corps = { ...fiche, review_at: fiche.review_at || null };
      if (fiche.id) {
        await axiosInstance.put(`/tasks_model/${fiche.id}/`, corps);
      } else {
        await axiosInstance.post('/tasks_model/', corps);
      }
      setFiche(null);
      setMessage('Modèle enregistré');
      charger();
    } catch {
      setErreur("Le modèle n'a pas pu être enregistré.");
    } finally {
      setOccupe(false);
    }
  };

  const supprimer = async () => {
    setOccupe(true);
    try {
      await axiosInstance.delete(`/tasks_model/${aSupprimer.id}/`);
      setASupprimer(null);
      setMessage('Modèle supprimé');
      charger();
    } catch {
      setErreur("Le modèle n'a pas pu être supprimé.");
    } finally {
      setOccupe(false);
    }
  };

  return (
    <>
      <Fiche
        titre="Modèles de tâches"
        explication="Une tâche toute prête. Regroupées en liste, elles se posent d'un coup sur un dossier."
        actions={(
          <Button size="small" variant="contained" startIcon={<BsPlusLg />}
                  onClick={() => setFiche({ ...VIDE })}>
            Nouveau modèle
          </Button>
        )}
        sx={{ p: 0 }}
      >
        {erreur && <Alert severity="error" sx={{ m: 2 }}
                          onClose={() => setErreur('')}>{erreur}</Alert>}

        {chargement && (
          <Stack spacing={1} sx={{ p: 2 }}>
            {[...Array(3)].map((_, i) => <Skeleton key={i} height={36} />)}
          </Stack>
        )}

        {!chargement && modeles.length === 0 && (
          <Typography variant="body2" color="text.secondary"
                      sx={{ py: 4, textAlign: 'center' }}>
            Aucun modèle pour l'instant.
          </Typography>
        )}

        {!chargement && modeles.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tâche</TableCell>
                  <TableCell>Destinataire</TableCell>
                  <TableCell>Échéance</TableCell>
                  <TableCell>Statut posé</TableCell>
                  <TableCell align="right">&nbsp;</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modeles.map((modele) => (
                  <TableRow key={modele.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center"
                             flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {modele.title}
                        </Typography>
                        {modele.hierarchy === 'EMAIL' && (
                          <Chip size="small" color="warning" label="courriel" />
                        )}
                        {modele.public && (
                          <Chip size="small" variant="outlined"
                                label="partagée" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {modele.receiver || <Box component="span"
                                               sx={{ color: 'text.disabled' }}>—</Box>}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {dateCourte(modele.review_at)
                        || <Box component="span"
                                sx={{ color: 'text.disabled' }}>—</Box>}
                    </TableCell>
                    <TableCell>
                      <Chip size="small"
                            color={statut(modele.status)?.couleur || 'default'}
                            label={statut(modele.status)?.libelle
                                   || modele.status} />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Modifier">
                        <IconButton size="small"
                                    onClick={() => setFiche({
                                      ...VIDE, ...modele,
                                      review_at: (modele.review_at || '')
                                        .split('T')[0] })}>
                          <BsPencil />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small"
                                    onClick={() => setASupprimer(modele)}>
                          <BsTrash />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Fiche>

      <ThemeProvider theme={themeExpert}>
        <Dialog open={fiche !== null} onClose={() => setFiche(null)}
                fullWidth maxWidth="md">
          <Box component="form" onSubmit={enregistrer} noValidate>
            <DialogTitle>
              {fiche?.id ? 'Modifier le modèle' : 'Nouveau modèle de tâche'}
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={estCourriel ? 'courriel' : 'tache'}
                  onChange={(_, valeur) => valeur && setFiche((s) => (
                    valeur === 'courriel'
                      ? { ...s, hierarchy: 'EMAIL',
                          sender: s.receiver || '', receiver: '' }
                      : { ...s, hierarchy: '',
                          receiver: s.sender || '', sender: '' }))}
                >
                  <ToggleButton value="tache">Tâche</ToggleButton>
                  <ToggleButton value="courriel">Courriel</ToggleButton>
                </ToggleButtonGroup>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField label="Titre" value={fiche?.title || ''}
                             onChange={poser('title')} required autoFocus />
                  <TextField select label="Statut posé"
                             value={fiche?.status || 'notstart'}
                             onChange={poser('status')}
                             sx={{ minWidth: 200 }}
                             helperText="L'état de la tâche à sa création.">
                    {STATUTS.map(({ code, libelle }) => (
                      <MenuItem key={code} value={code}>{libelle}</MenuItem>
                    ))}
                  </TextField>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
                       alignItems={{ sm: 'center' }}>
                  <ChampDate label="Échéance"
                             value={fiche?.review_at || ''}
                             onChange={poser('review_at')}
                             InputLabelProps={{ shrink: true }}
                             sx={{ maxWidth: { sm: 220 } }}
                             helperText="Une date fixe : à revoir si le modèle sert longtemps." />
                  <FormControlLabel
                    control={<Switch checked={Boolean(fiche?.public)}
                                     onChange={poser('public')} />}
                    label="Visible par ceux avec qui le dossier est partagé"
                  />
                </Stack>

                {!estCourriel && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField label="Assignée à" value={fiche?.receiver || ''}
                               onChange={poser('receiver')} />
                    <TextField label="Hiérarchie"
                               value={fiche?.hierarchy || ''}
                               onChange={poser('hierarchy')} />
                  </Stack>
                )}

                {estCourriel && (
                  <>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField label="Expéditeur" value={fiche?.sender || ''}
                                 onChange={poser('sender')} />
                      <TextField label="Destinataire"
                                 value={fiche?.receiver || ''}
                                 onChange={poser('receiver')} />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField label="Copie à"
                                 value={fiche?.copy_receiver || ''}
                                 onChange={poser('copy_receiver')} />
                      <TextField label="Objet" value={fiche?.title_bis || ''}
                                 onChange={poser('title_bis')} />
                    </Stack>
                  </>
                )}

                <TextField select label="Partir d'un modèle de courrier"
                           value=""
                           onChange={(e) => appliquerCourrier(e.target.value)}
                           helperText="Son contenu remplacera le texte ci-dessous.">
                  <MenuItem value=""><em>Aucun</em></MenuItem>
                  {courriers.map((courrier) => (
                    <MenuItem key={courrier.id} value={courrier.id}>
                      {courrier.title}
                    </MenuItem>
                  ))}
                </TextField>

                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {estCourriel ? 'Corps du message' : 'Commentaire'}
                  </Typography>
                  <Box sx={{ '& .ql-container': { minHeight: 240 },
                             '& .ql-editor': { minHeight: 240 } }}>
                    <ReactQuill theme="snow" value={fiche?.comment || ''}
                                onChange={(html) => setFiche(
                                  (s) => ({ ...s, comment: html }))}
                                modules={OUTILS} />
                  </Box>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setFiche(null)} disabled={occupe}>
                Annuler
              </Button>
              <Button type="submit" variant="contained"
                      disabled={occupe || !fiche?.title?.trim()}>
                {occupe ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>

        <Dialog open={aSupprimer !== null} onClose={() => setASupprimer(null)}>
          <DialogTitle>Supprimer ce modèle ?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              « {aSupprimer?.title} » disparaîtra des listes de tâches qui le
              contiennent. Les tâches déjà posées sur des dossiers ne bougent
              pas.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setASupprimer(null)} disabled={occupe}>
              Annuler
            </Button>
            <Button onClick={supprimer} color="error" variant="contained"
                    disabled={occupe}>
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>

      <Snackbar open={Boolean(message)} autoHideDuration={3000}
                onClose={() => setMessage('')} message={message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </>
  );
}

export default TaskModelView;
